import { useEffect, useState } from "react";

const TEXT_ENTRY_SELECTOR =
  "textarea, input:not([type=checkbox], [type=radio], [type=button], [type=submit], [type=file], [type=range], [type=color])";

function isTextEntry(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.matches(TEXT_ENTRY_SELECTOR) || target.isContentEditable === true);
}

/** True while the focus is in a field that the on-screen keyboard types into. */
export default function useIsTyping(): boolean {
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    // Safari and Firefox send no focusout when the focused field is removed from the page
    // (a dialog with an input closes, the route changes). The next tap finds out that the
    // focus is gone, so the bar does not stay hidden until some other field is focused.
    const syncWithFocus = () => setIsTyping(isTextEntry(document.activeElement));
    const onFocusIn = (event: FocusEvent) => setIsTyping(isTextEntry(event.target));
    const onFocusOut = (event: FocusEvent) => setIsTyping(isTextEntry(event.relatedTarget));

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("pointerdown", syncWithFocus);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("pointerdown", syncWithFocus);
    };
  }, []);

  return isTyping;
}
